/**
 * Maintenance Scheduling Utilities
 * Per-item next-due calculation logic for the finalized maintenance system
 */

/**
 * Calculate the next due date for a specific equipment item
 * Based on its maintenance history and applicable maintenance jobs
 * 
 * @param {Object} equipment - Equipment item
 * @param {Array} maintenanceJobs - All maintenance jobs
 * @param {Array} maintenanceHistory - Maintenance history records for this item
 * @returns {Object} Next due information
 */
export function calculateNextDue(equipment, maintenanceJobs, maintenanceHistory) {
  if (!equipment || !maintenanceJobs || !Array.isArray(maintenanceJobs)) {
    return {
      type: 'Unknown',
      date: null,
      daysUntil: null,
      status: 'unknown',
      urgent: false
    };
  }

  // Find applicable maintenance jobs for this equipment
  const applicableJobs = maintenanceJobs.filter(job => 
    isJobApplicableToEquipment(job, equipment)
  );

  if (applicableJobs.length === 0) {
    return {
      type: 'No Schedule',
      date: null,
      daysUntil: null,
      status: 'unscheduled',
      urgent: false
    };
  }

  // Calculate next due for each applicable job
  const nextDueItems = applicableJobs.map(job => {
    const lastService = getLastServiceForJob(job, maintenanceHistory);
    const nextDue = calculateJobNextDue(job, lastService);
    
    return {
      jobId: job.id,
      jobLabel: job.item || job.label,  // Use job.item (modern) or job.label (legacy)
      ...nextDue
    };
  });

  // Find the most urgent (earliest) next due
  const mostUrgent = nextDueItems
    .filter(item => item.date)
    .sort((a, b) => new Date(a.date) - new Date(b.date))[0];

  if (!mostUrgent) {
    // No services have been performed yet
    const firstJob = applicableJobs
      .sort((a, b) => (a.intervalMonths || 12) - (b.intervalMonths || 12))[0];
    
    return {
      type: firstJob.item || firstJob.label,  // Use job.item (modern) or job.label (legacy)
      date: null,
      daysUntil: null,
      status: 'never_serviced',
      urgent: true,
      overdue: false
    };
  }

  const today = new Date();
  const nextDate = new Date(mostUrgent.date);
  const daysUntil = Math.ceil((nextDate - today) / (1000 * 60 * 60 * 24));
  
  // Determine urgency and status
  let status = 'ok';
  let urgent = false;
  let overdue = false;

  if (daysUntil < 0) {
    status = 'overdue';
    urgent = true;
    overdue = true;
  } else if (daysUntil <= 7) {
    status = 'due_this_week';
    urgent = true;
  } else if (daysUntil <= 30) {
    status = 'due_this_month';
    urgent = false;
  } else if (daysUntil <= 90) {
    status = 'due_soon';
    urgent = false;
  }

  return {
    type: mostUrgent.jobLabel,
    date: mostUrgent.date,
    daysUntil,
    status,
    urgent,
    overdue
  };
}

/**
 * Check if a maintenance job applies to a specific equipment item
 * @param {Object} job - Maintenance job
 * @param {Object} equipment - Equipment item
 * @returns {boolean}
 */
function isJobApplicableToEquipment(job, equipment) {
  // PRIORITY 1: Check if job is explicitly linked to this equipment (modern pattern)
  if (job.linkedEquipmentIds && Array.isArray(job.linkedEquipmentIds)) {
    return job.linkedEquipmentIds.includes(equipment.id);
  }

  // PRIORITY 2: Check if job has equipment filter (legacy pattern)
  if (job.equipmentFilter) {
    const filter = job.equipmentFilter;
    
    // Check category match
    if (filter.category && equipment.category) {
      if (filter.category.toLowerCase() !== equipment.category.toLowerCase()) {
        return false;
      }
    }
    
    // Check type match
    if (filter.types && Array.isArray(filter.types)) {
      const equipmentType = equipment.type?.toLowerCase() || '';
      const hasTypeMatch = filter.types.some(type => 
        equipmentType.includes(type.toLowerCase()) ||
        equipment.name?.toLowerCase().includes(type.toLowerCase())
      );
      if (!hasTypeMatch) {
        return false;
      }
    }
    
    // Check detector type match for precision (CO Detection specific)
    if (filter.detectorTypes && Array.isArray(filter.detectorTypes)) {
      const equipmentName = equipment.name?.toLowerCase() || '';
      const equipmentType = equipment.type?.toLowerCase() || '';
      
      const hasDetectorTypeMatch = filter.detectorTypes.some(detectorType => {
        const type = detectorType.toLowerCase();
        return equipmentName.includes(type) || 
               equipmentType.includes(type) ||
               (type === 'co' && (equipmentName.includes('carbon monoxide') || equipmentName.includes('co detector'))) ||
               (type === 'gas' && equipmentName.includes('hydrocarbon')) ||
               (type === 'fire' && (equipmentName.includes('smoke') || equipmentName.includes('heat') || equipmentName.includes('flame')));
      });
      
      if (!hasDetectorTypeMatch) {
        return false;
      }
    }
    
    // Check location match (if specified)
    if (filter.locations && filter.locations.length > 0) {
      const equipmentLocation = equipment.location?.toLowerCase() || '';
      const hasLocationMatch = filter.locations.some(loc =>
        equipmentLocation.includes(loc.toLowerCase())
      );
      if (!hasLocationMatch) {
        return false;
      }
    }
    
    return true;
  }
  
  // Fallback: match by equipment type string
  if (job.equipmentType) {
    const jobType = job.equipmentType.toLowerCase();
    const equipmentType = equipment.type?.toLowerCase() || '';
    const equipmentName = equipment.name?.toLowerCase() || '';
    
    return equipmentType.includes(jobType) || 
           equipmentName.includes(jobType) ||
           jobType.includes(equipmentType);
  }
  
  return false;
}

/**
 * Get the last service date for a specific job type from maintenance history
 * @param {Object} job - Maintenance job
 * @param {Array} history - Maintenance history records
 * @returns {Object|null} Last service record or null
 */
function getLastServiceForJob(job, history) {
  if (!history || !Array.isArray(history)) {
    return null;
  }
  
  // Look for records that match this job
  const matchingRecords = history.filter(record => {
    // Match by job ID if available
    if (record.jobId && record.jobId === job.id) {
      return true;
    }
    
    // Match by job type/title
    if (record.checkType && job.label) {
      return record.checkType.toLowerCase().includes(job.label.toLowerCase()) ||
             job.label.toLowerCase().includes(record.checkType.toLowerCase());
    }
    
    return false;
  });
  
  if (matchingRecords.length === 0) {
    return null;
  }
  
  // Return the most recent record
  return matchingRecords
    .sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0))[0];
}

/**
 * Calculate next due date for a specific job based on last service
 * @param {Object} job - Maintenance job
 * @param {Object|null} lastService - Last service record
 * @returns {Object} Next due calculation
 */
function calculateJobNextDue(job, lastService) {
  const intervalMonths = job.intervalMonths || 12;
  
  if (!lastService) {
    // No service history - should be done ASAP
    return {
      date: new Date().toISOString().split('T')[0], // Today
      fromDate: null
    };
  }
  
  // Calculate next due based on last service
  const lastDate = lastService.date ? new Date(lastService.date) : 
                   lastService.completedAt ? new Date(lastService.completedAt) :
                   new Date();
  
  const nextDate = new Date(lastDate);
  nextDate.setMonth(nextDate.getMonth() + intervalMonths);
  
  return {
    date: nextDate.toISOString().split('T')[0],
    fromDate: lastDate.toISOString().split('T')[0]
  };
}

/**
 * Get maintenance status summary for equipment
 * @param {Object} equipment - Equipment item
 * @param {Array} maintenanceJobs - All maintenance jobs
 * @param {Array} maintenanceHistory - Maintenance history for this item
 * @returns {Object} Status summary
 */
export function getMaintenanceStatus(equipment, maintenanceJobs, maintenanceHistory) {
  const nextDue = calculateNextDue(equipment, maintenanceJobs, maintenanceHistory);
  
  // Map status to display information
  const statusMap = {
    'overdue': {
      label: 'Overdue',
      color: 'bg-red-100 text-red-800',
      iconColor: 'text-red-600'
    },
    'due_this_week': {
      label: 'Due This Week',
      color: 'bg-orange-100 text-orange-800',
      iconColor: 'text-orange-600'
    },
    'due_this_month': {
      label: 'Due This Month',
      color: 'bg-yellow-100 text-yellow-800',
      iconColor: 'text-yellow-600'
    },
    'due_soon': {
      label: 'Due Soon',
      color: 'bg-blue-100 text-blue-800',
      iconColor: 'text-blue-600'
    },
    'ok': {
      label: 'OK',
      color: 'bg-green-100 text-green-800',
      iconColor: 'text-green-600'
    },
    'never_serviced': {
      label: 'Never Serviced',
      color: 'bg-gray-100 text-gray-800',
      iconColor: 'text-gray-600'
    },
    'unscheduled': {
      label: 'No Schedule',
      color: 'bg-gray-100 text-gray-800',
      iconColor: 'text-gray-600'
    },
    'unknown': {
      label: 'Unknown',
      color: 'bg-gray-100 text-gray-800',
      iconColor: 'text-gray-600'
    }
  };
  
  return {
    ...nextDue,
    display: statusMap[nextDue.status] || statusMap.unknown
  };
}

/**
 * Format days until display text
 * @param {number|null} daysUntil - Days until due
 * @param {boolean} overdue - Whether overdue
 * @returns {string}
 */
export function formatDaysUntil(daysUntil, overdue = false) {
  if (daysUntil === null) {
    return 'Not scheduled';
  }
  
  if (overdue) {
    return `Overdue by ${Math.abs(daysUntil)} day${Math.abs(daysUntil) === 1 ? '' : 's'}`;
  }
  
  if (daysUntil === 0) {
    return 'Due today';
  }
  
  if (daysUntil === 1) {
    return 'Due tomorrow';
  }
  
  if (daysUntil <= 7) {
    return `Due in ${daysUntil} days`;
  }
  
  if (daysUntil <= 30) {
    const weeks = Math.ceil(daysUntil / 7);
    return `Due in ${weeks} week${weeks === 1 ? '' : 's'}`;
  }
  
  if (daysUntil <= 365) {
    const months = Math.ceil(daysUntil / 30);
    return `Due in ${months} month${months === 1 ? '' : 's'}`;
  }
  
  const years = Math.floor(daysUntil / 365);
  const remainingDays = daysUntil % 365;
  const months = Math.ceil(remainingDays / 30);
  
  if (months === 0) {
    return `Due in ${years} year${years === 1 ? '' : 's'}`;
  }
  
  return `Due in ${years}y ${months}m`;
}